/** Purchasing inquiry drafts: explicit user text, no order or source mutation.
 * Browser-only drafts are scoped by actor/tenant/document by the UI owner.
 * Evidence comes exclusively from the current offer comparison; no signed URLs.
 */
import { isComparablePosition } from "./offerPositionKind";
import { formatDateShort } from './format';
import type { ReviewOffer, ReviewLine } from './offerComparison';
import { money } from './offerComparison';
import { offerCsv } from './offerCsv';
import { targetPrice, positiveDifferenceAmount, type PurchasingTarget } from './purchasingTarget';

export type InquiryCandidate = { id: string; line: ReviewLine; target: number; difference: number };
export type InquiryDraft = { version: 1; source: string; selected: string[]; textSelection: string[]; text: string; stage: 'choose' | 'write' };

export type InquiryMemory = Map<string, { draft: InquiryDraft; persisted: boolean; offerId: string }>;

export function inquiryCandidates(offer: ReviewOffer, target: PurchasingTarget): InquiryCandidate[] {
  if (offer.preview.history_comparison_blocker || !offer.preview.currency?.trim()) return [];
  return offer.preview.lines.flatMap(line => {
    const price = targetPrice(line.reference?.price, target.discountPct);
    if (!isComparablePosition(line) || line.comparison_warning || !line.reference || price === null
      || !line.uom?.trim() || !line.reference.doc_id || !line.reference.doc_number
      || !line.qty || Number(line.qty) <= 0 || !Number.isFinite(Number(line.qty))
      || !line.net_unit_price || !Number.isFinite(Number(line.net_unit_price)) || Number(line.net_unit_price) <= price
      || line.line_net_amount === null || !Number.isFinite(Number(line.line_net_amount))
      || offer.preview.lines.filter(other => other.line_pos === line.line_pos).length !== 1) return [];
    const difference = positiveDifferenceAmount(line.net_unit_price, price, line.qty);
    return difference === null ? [] : [{ id: line.line_pos, line, target: price, difference }];
  });
}

export function inquirySource(offer: ReviewOffer, target: PurchasingTarget): string {
  // Whitelist fields: never persist PDF/media access tokens or blob URLs.
  return JSON.stringify({ id: offer.docId ?? offer.id, sha: offer.preview.source_sha256,
    number: offer.preview.doc_number, supplier: offer.preview.supplier, date: offer.preview.doc_date,
    currency: offer.preview.currency, referenceDate: offer.preview.reference_date, referenceBasis: offer.preview.reference_basis, target,
    lines: offer.preview.lines.map(line => ({ position: line.line_pos, article: line.article_code,
      description: line.description, qty: line.qty, unit: line.uom, price: line.net_unit_price,
      total: line.line_net_amount, note: line.source_note, warning: line.comparison_warning,
      reference: line.reference ? { id: line.reference.doc_id, number: line.reference.doc_number,
        position: line.reference.line_pos, date: line.reference.doc_date, price: line.reference.price } : null })) });
}

export function inquiryText(offer: ReviewOffer, rows: InquiryCandidate[]): string {
  const currency = offer.preview.currency;
  return [
    `Betreff: Rückfrage zu Angebot ${offer.preview.doc_number ?? offer.name}`,
    '', 'Guten Tag,', '',
    `bitte prüfen Sie die Preise für folgende Positionen Ihres Angebots ${offer.preview.doc_number ?? offer.name}${offer.preview.doc_date ? ` vom ${formatDateShort(offer.preview.doc_date)}` : ''}:`, '',
    ...rows.map(({ line, target }) => `${line.printed_position === null ? "Zeile" : "Pos."} ${line.line_pos} · ${line.article_code ?? 'Artikelnummer offen'} · ${line.description}\nMenge: ${line.qty} ${line.uom}; Angebot: ${money(line.net_unit_price, currency)} / ${line.uom}; gewünschter Preis: ${money(target, currency)} / ${line.uom} netto.`),
    '', 'Bitte bestätigen Sie, ob Sie uns diese Preise anbieten können. Bitte nennen Sie auch abweichende Liefer- oder Angebotsbedingungen.',
    '', 'Vielen Dank.',
  ].join('\n');
}

export function inquiryEvidenceCsv(offer: ReviewOffer, target: PurchasingTarget, rows: InquiryCandidate[]): string {
  return offerCsv([
    ['Angebot', 'Angebotsdatum', 'Lieferant', 'Position', 'Artikel', 'Beschreibung', 'Menge', 'Einheit', 'Währung', 'Angebotspreis netto', 'Gewünschter Zielpreis netto', 'Referenzpreis', 'Referenzrechnung', 'Referenzposition', 'Referenzdatum', 'Berechnungsstichtag', 'Abschlag %', 'Referenzbasis', 'Grundlage', 'Vorgabe-Version'],
    ...rows.map(({ line, target: price }) => [offer.preview.doc_number ?? offer.name, offer.preview.doc_date,
      offer.preview.supplier, line.line_pos, line.article_code, line.description, line.qty, line.uom,
      offer.preview.currency, line.net_unit_price, price.toFixed(2), line.reference?.price,
      line.reference?.doc_number, line.reference?.line_pos, line.reference?.doc_date,
      offer.preview.reference_date, target.discountPct, target.basis === "last" ? "Letzter Einkauf · 12 Monate" : "Bester Einkauf · 12 Monate", { company: "Betriebsvorgabe", own: "Eigene Berechnung", history: "Bisherige Einkäufe" }[target.origin], target.revision]),
  ]);
}

export function readInquiry(key: string | null): InquiryDraft | null {
  if (!key) return null;
  try {
    const value = JSON.parse(localStorage.getItem(key) ?? 'null') as InquiryDraft | null;
    if (!value || value.version !== 1 || typeof value.source !== 'string' || typeof value.text !== 'string'
      || !['choose', 'write'].includes(value.stage) || !Array.isArray(value.selected)
      || !Array.isArray(value.textSelection) || !value.textSelection.every(id => typeof id === 'string')
      || !value.selected.every(id => typeof id === 'string') || new Set(value.selected).size !== value.selected.length) return null;
    return value;
  } catch { return null; }
}

export function downloadInquiryText(text: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'angebots-rueckfrage.txt'; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
